/**
 * Column definitions for the roster table.
 *
 * Column ids double as the service's `sort_by` values (`PLAYER_SORT_FIELDS`),
 * so a sortable header needs no translation layer. The leading checkbox column
 * only exists for personas that may bulk-delete — nobody is offered a selection
 * they cannot act on.
 */
import type { DataTableColumn } from '../../components/data-table';
import type { Player } from '../../lib/api-types';

/** Label + code + a retired marker when the catalog row was deprecated. */
function cohortCell(player: Player): JSX.Element {
  return (
    <span className="roster-cell">
      <span>{player.cohort_label ?? player.cohort}</span>
      <span className="muted roster-cell__code">{player.cohort}</span>
      {player.cohort_is_deprecated ? <span className="badge">Retired</span> : null}
    </span>
  );
}

export interface RosterColumnOptions {
  selectedIds: ReadonlySet<string>;
  onToggle: (player: Player, isSelected: boolean) => void;
  /** Adds the multi-select column (administrators only). */
  canSelect: boolean;
}

export function buildRosterColumns({
  selectedIds,
  onToggle,
  canSelect,
}: RosterColumnOptions): DataTableColumn<Player>[] {
  const columns: DataTableColumn<Player>[] = [
    {
      id: 'jersey_number',
      header: 'Jersey',
      isSortable: true,
      isPinned: true,
      align: 'end',
      minWidth: 96,
      maxWidth: 96,
      grow: 0,
      render: (player) => player.jersey_number ?? '—',
    },
    {
      id: 'last_name',
      header: 'Name',
      isSortable: true,
      isPinned: true,
      minWidth: 160,
      maxWidth: 320,
      grow: 3,
      render: (player) => <span className="roster-cell__name">{player.full_name}</span>,
    },
    {
      id: 'cohort',
      header: 'Cohort',
      isSortable: true,
      priority: 1,
      minWidth: 150,
      maxWidth: 260,
      grow: 2,
      render: cohortCell,
    },
    {
      id: 'position',
      header: 'Position',
      isSortable: true,
      priority: 2,
      minWidth: 90,
      maxWidth: 140,
      grow: 1,
      render: (player) => player.position ?? '—',
    },
    {
      id: 'school',
      header: 'School',
      priority: 3,
      minWidth: 140,
      grow: 2,
      render: (player) => player.school ?? '—',
    },
  ];
  if (!canSelect) return columns;
  return [
    {
      id: 'select',
      header: 'Select',
      isPinned: true,
      minWidth: 40,
      maxWidth: 40,
      grow: 0,
      renderHeader: () => <span className="visually-hidden">Select</span>,
      render: (player) => (
        <input
          type="checkbox"
          checked={selectedIds.has(player.player_id)}
          aria-label={`Select ${player.full_name}`}
          onChange={(event) => onToggle(player, event.target.checked)}
        />
      ),
    },
    ...columns,
  ];
}
